import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { Box, Button, ButtonGroup, TextButton } from '@zillow/constellation-10';
import { IconArrowLeftOutline, IconArrowRightOutline } from '@zillow/constellation-icons';

import { useRentalManagerUserClaimWebApiHandler_propertyLookupAll } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import stringHelper from '../../utils/stringHelper';

import { ROUTES } from '../../constants/urls';
import { STEP } from './constants';

import ViewClaimListingsModal from '../modals/ViewClaimListingsModal';

import HomeSearch from '../../assets/HomeSearch.svg';
import PersonProfile from '../../assets/PersonProfile.svg';

import WizardBodyTemplate from '../WizardBodyTemplate';

import type { WizardBodyTemplateProps } from '../WizardStepController';

const { pluralize } = stringHelper;

const WizVerifyContactSuccess = ({ onGoToPrevStep, onGoToStep, progressStepper }: WizardBodyTemplateProps) => {
  const router = useRouter();
  const [showViewListingsModal, setShowViewListingsModal] = useState(false);

  const {
    data,
    isFetching: isTransitioning,
    isError: hasError,
  } = useRentalManagerUserClaimWebApiHandler_propertyLookupAll({
    includeVerified: true,
    includeUnverified: false,
  });

  const listingCount = data?.totalListingCount ?? null;
  const contactMethodsCount = data?.totalContactMethodCount ?? 0;

  const handleViewListings = () => {
    setShowViewListingsModal(true);
  };

  const handleFindListings = () => {
    onGoToStep(STEP.FIND_LISTING_RESULTS);
  };

  const handleViewProperties = () => {
    router.push(ROUTES.PROPERTIES);
  };

  const renderSubtitle = () =>
    listingCount != null ? (
      <>
        {`Congratulations! You have verified ${pluralize(contactMethodsCount, 'contact', 'contacts')} and we’ve 
            linked ${pluralize(listingCount, 'listing', 'listings')} to your account. `}
        <TextButton onClick={handleViewListings} css={{ fontWeight: '600' }}>
          View your listings
        </TextButton>
      </>
    ) : (
      <>
        {`No listings are associated with the contact you verified.
             To link listings, search by other contact methods.`}
      </>
    );

  const renderDescription = () =>
    listingCount != null ? (
      <>
        We’ll continue searching for listings with this contact information. New verified listings will appear on your
        Zillow Rental Manager dashboard.
      </>
    ) : null;

  const renderIcon = () => (
    <Image
      src={listingCount != null ? PersonProfile : HomeSearch}
      alt={listingCount != null ? 'A business person' : 'Searching for listings'}
      fill
    />
  );

  return (
    <WizardBodyTemplate
      title={listingCount != null ? `You’ve claimed ${pluralize(listingCount, 'listing', 'listings')}` : ''}
      subtitle={renderSubtitle()}
      description={renderDescription()}
      isTransitioning={isTransitioning}
      hasError={hasError}
      icon={renderIcon()}
      progressStepper={progressStepper}
      footer={
        <ButtonGroup
          css={{ marginTop: 'auto', paddingTop: '600', justifyContent: 'center' }}
          aria-label="Find listing success"
        >
          <Button
            tone="brand"
            emphasis="outlined"
            onClick={onGoToPrevStep ?? undefined}
            disabled={!onGoToPrevStep}
            icon={<IconArrowLeftOutline />}
          >
            Back
          </Button>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleViewProperties}
            icon={<IconArrowRightOutline />}
            iconPosition="end"
            css={{ animation: 'fadeIn ease 2s', alignSelf: 'center' }}
          >
            View properties
          </Button>
        </ButtonGroup>
      }
    >
      {listingCount == null && (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Button tone="brand" emphasis="filled" css={{ marginTop: '200' }} onClick={handleFindListings}>
            Find listings
          </Button>
        </Box>
      )}
      {showViewListingsModal && (
        <ViewClaimListingsModal
          isOpen={showViewListingsModal}
          onClose={() => setShowViewListingsModal(false)}
          includeUnverified={false}
        />
      )}
    </WizardBodyTemplate>
  );
};

export default WizVerifyContactSuccess;
